import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import {
  CATEGORIAS_COMISION,
  CATEGORIAS_GASTO,
  nuevoId,
  type BaseCalculo,
  type Cargo,
  type Periodicidad,
  type TipoCargo,
  type TipoImporte,
} from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";

const PERIODICIDADES: Periodicidad[] = [
  "Única vez",
  "A la fecha de firma",
  "Por evento de mora",
  "Mensual",
  "Por período",
];
const BASES: BaseCalculo[] = ["Monto financiado (circular)", "Importe fijo"];

export function ConfiguradorCargos() {
  const { productos, cargos, setCargos } = useOperativo();

  const set = (id: string, patch: Partial<Cargo>) =>
    setCargos(cargos.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const agregar = (productoId: string) =>
    setCargos([
      ...cargos,
      {
        id: nuevoId("cargo"),
        productoId,
        tipo: "Gasto",
        categoria: CATEGORIAS_GASTO[0]!,
        nombre: "Nuevo gasto",
        base: "Importe fijo",
        tipoImporte: "$",
        importe: 0,
        periodicidad: "Única vez",
        momento: "Al desembolso",
        financiado: true,
        causaIva: true,
        aplicar: true,
      },
    ]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
          Configurador de comisiones y gastos
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Catálogo CONDUSEF CAT-038 (comisiones) y CAT-043 (gastos). Los cargos financiados con base
          circular se resuelven algebraicamente sobre el monto financiado; los no financiados se
          cobran por fuera y sí afectan el CAT al reducir el monto recibido.
        </p>
      </div>

      {productos.map((p) => {
        const propios = cargos.filter((c) => c.productoId === p.id);
        return (
          <Card key={p.id} className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
                {p.nombre}
              </h3>
              <Button
                size="sm"
                variant="outline"
                className="no-print"
                onClick={() => agregar(p.id)}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Agregar cargo
              </Button>
            </div>
            <div className="divide-y">
              {propios.length === 0 && (
                <p className="px-5 py-6 text-sm text-muted-foreground">
                  Sin comisiones ni gastos configurados.
                </p>
              )}
              {propios.map((c) => (
                <div key={c.id} className="grid gap-3 px-5 py-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <Select
                      value={c.tipo}
                      onValueChange={(v) =>
                        set(c.id, {
                          tipo: v as TipoCargo,
                          categoria:
                            v === "Comisión" ? CATEGORIAS_COMISION[0]! : CATEGORIAS_GASTO[0]!,
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Comisión">Comisión</SelectItem>
                        <SelectItem value="Gasto">Gasto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Categoría</Label>
                    <Select value={c.categoria} onValueChange={(v) => set(c.id, { categoria: v })}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(c.tipo === "Comisión" ? CATEGORIAS_COMISION : CATEGORIAS_GASTO).map(
                          (k) => (
                            <SelectItem key={k} value={k}>
                              {k}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input
                      className="h-9"
                      value={c.nombre}
                      onChange={(e) => set(c.id, { nombre: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Base de cálculo</Label>
                    <Select
                      value={c.base}
                      onValueChange={(v) => set(c.id, { base: v as BaseCalculo })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BASES.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de importe</Label>
                    <Select
                      value={c.tipoImporte}
                      onValueChange={(v) => set(c.id, { tipoImporte: v as TipoImporte })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="%">% (porcentaje)</SelectItem>
                        <SelectItem value="$">$ fijo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Importe</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step={0.01}
                      className="h-9 num"
                      value={Number.isNaN(c.importe) ? "" : c.importe}
                      onChange={(e) => set(c.id, { importe: e.target.valueAsNumber })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Periodicidad</Label>
                    <Select
                      value={c.periodicidad}
                      onValueChange={(v) => set(c.id, { periodicidad: v as Periodicidad })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIODICIDADES.map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Momento de aplicación</Label>
                    <Input
                      className="h-9"
                      value={c.momento}
                      onChange={(e) => set(c.id, { momento: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-6 md:col-span-2 xl:col-span-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${c.id}-apl`}
                        checked={c.aplicar !== false}
                        onCheckedChange={(v) => set(c.id, { aplicar: v })}
                      />
                      <Label htmlFor={`${c.id}-apl`} className="text-xs">
                        ¿Aplicar? {c.aplicar !== false ? "Sí" : "No (calcula 0)"}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${c.id}-fin`}
                        checked={c.financiado}
                        onCheckedChange={(v) => set(c.id, { financiado: v })}
                      />
                      <Label htmlFor={`${c.id}-fin`} className="text-xs">
                        ¿Financiado? {c.financiado ? "Sí" : "No (cobro externo)"}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${c.id}-iva`}
                        checked={c.causaIva}
                        onCheckedChange={(v) => set(c.id, { causaIva: v })}
                      />
                      <Label htmlFor={`${c.id}-iva`} className="text-xs">
                        ¿Causa IVA? {c.causaIva ? "Sí" : "No"}
                      </Label>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="no-print ml-auto text-destructive hover:text-destructive"
                      onClick={() => setCargos(cargos.filter((x) => x.id !== c.id))}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" /> Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
